import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import TransactionTable from "./transaction-table";
import BalanceHistoryChart from "./balance-history-chart";
import { AddressData } from "@shared/schema";
import { formatBitcoin, formatDateRange } from "@/lib/utils";
import { Download, Copy, TrendingUp, TrendingDown, Wallet, Bitcoin, LoaderCircle, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

import { handleUsageLimitError, useUsageLimitStore } from "@/stores/usageLimitStore";
import { useQuery } from "@tanstack/react-query";
import { fetchUserUsageStats } from "@/data-access/users";
import { useAuth } from "@/hooks/use-auth";
import { createExportJob } from "@/data-access/exports";
import { pollExportJob } from "@/lib/poll-export-job";

interface WalletSummaryProps {
  data: AddressData;
  onLoadMore?: () => void;
  onReset?: () => void;
  currentPageToken?: string | null;
  isLoadingMore?: boolean;
}

export default function WalletSummary({ data, onLoadMore, onReset, currentPageToken, isLoadingMore }: WalletSummaryProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  // Track the current transaction filter selected in the child TransactionTable ("all", "received", or "sent")
  const [filterType, setFilterType] = useState<"all" | "received" | "sent">("all");
  const exportCancelledRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    return () => { exportCancelledRef.current = true; };
  }, []);
  const { user } = useAuth();

  // Get usage stats for current user
  const { data: usageStats } = useQuery({
    queryKey: ['/api/users/usage-stats'],
    queryFn: fetchUserUsageStats,
    enabled: !!user, // Only fetch if user is authenticated
  });

  const { showModal } = useUsageLimitStore();

  // Calculate the percentage of received vs sent in the period
  const totalInPeriod = data.stats.periodStats ? 
    data.stats.periodStats.received + data.stats.periodStats.sent : 0;
  
  const receivedPercentage = totalInPeriod > 0 && data.stats.periodStats ? 
    Math.round((data.stats.periodStats.received / totalInPeriod) * 100) : 0;
  
  const sentPercentage = 100 - receivedPercentage;

  const handleDownloadCsv = async () => {
    // Check usage limit before proceeding
    if (usageStats && usageStats.usage.walletExports >= 30 && !usageStats.isAdmin) {
      showModal({
        usageType: 'wallet_exports',
        current: usageStats.usage.walletExports,
        limit: 30,
        resetDate: usageStats.resetDate,
      });
      return;
    }

    setIsGenerating(true);

    try {
      const { jobId } = await createExportJob({
        addresses: [data.address],
        startDate: data.startDate || null,
        endDate: data.endDate || null,
        columns: filterType,
        source: "wallet_exports",
        fileName: null,
      });

      exportCancelledRef.current = false;
      await pollExportJob(jobId, {
        onProgress: () => {},
        onCompleted: () => {
          toast({
            title: "Export successful",
            description: `Report for address ${data.address.substring(0, 8)}... downloaded`,
          });
          setIsGenerating(false);
        },
        onFailed: (msg) => {
          toast({ title: "Export failed", description: msg, variant: "destructive" });
          setIsGenerating(false);
        },
        onTimeout: () => {
          toast({
            title: "Export timed out",
            description: "The export is taking longer than expected. Check your export history for the result.",
            variant: "destructive",
          });
          setIsGenerating(false);
        },
        isCancelled: () => exportCancelledRef.current,
      });
    } catch (error: any) {
      console.error("Error exporting address:", error);

      if (error.status === 429 && error.data?.usageType) {
        handleUsageLimitError(error.data);
      } else if (error.status === 409) {
        toast({
          title: "Export in progress",
          description: "You already have an export in progress. Please wait for it to complete.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Export failed",
          description: error.message || "Failed to generate CSV export",
          variant: "destructive",
        });
      }
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const dateRange = formatDateRange(data.startDate, data.endDate);

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            Wallet Summary
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-warning text-warning-foreground text-xs font-medium">
              <Bitcoin className="h-3 w-3 mr-1" /> Bitcoin
            </span>
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Total Received Card */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-2 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-success/10 rounded-lg">
                <TrendingUp className="h-4 w-4 text-success" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Received</p>
                {data.stats.periodStats && (
                  <p className="text-xs text-muted-foreground">
                    Period: ₿ {formatBitcoin(data.stats.periodStats.received)}
                  </p>
                )}
              </div>
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">₿ {formatBitcoin(data.stats.totalReceived)}</p>
        </div>
        
        {/* Total Sent Card */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-2 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-warning/10 rounded-lg">
                <TrendingDown className="h-4 w-4 text-warning" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Sent</p>
                {data.stats.periodStats && (
                  <p className="text-xs text-muted-foreground">
                    Period: ₿ {formatBitcoin(data.stats.periodStats.sent)}
                  </p>
                )}
              </div>
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">₿ {formatBitcoin(data.stats.totalSent)}</p>
        </div>
        
        {/* Current Balance Card */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-2 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Wallet className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Current Balance</p>
                <p className="text-xs text-muted-foreground">
                  Transactions: {data.stats.transactionCount}
                </p>
              </div>
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">₿ {formatBitcoin(data.stats.currentBalance)}</p>
        </div>
      </div>
      
      {/* Transaction Table - disable internal pagination when we have external API pagination */}
      <TransactionTable 
        transactions={data.transactions} 
        disableInternalPagination={true}
        onFilterTypeChange={(value) => setFilterType(value)}
        onExportCsv={handleDownloadCsv}
        isExporting={isGenerating}
      />
      
      {/* Load More Controls */}
      {data.pagination && (
        <div className="flex justify-center mt-6 space-x-2">

          <Button
            onClick={onLoadMore}
            disabled={!data.pagination.hasNextPage || isLoadingMore}
            variant="default"
            size="sm"
          >
            {isLoadingMore ? (
              <>
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More Transactions'
            )}
          </Button>
          
          <span className="text-sm text-muted-foreground self-center ml-4">
            Showing {data.transactions.length} transactions ({data.pagination.itemsPerPage} per page)
          </span>
        </div>
      )}
      
      {/* Balance History Chart */}
      {/* <BalanceHistoryChart transactions={data.transactions} /> */}
    </div>
  );
}
